import Empty from '@/assets/placeholders/empty.png';

import { WhiteLabelTheme } from '../types';

import CardBackground from './assets/card-background.svg';
import CardLogo from './assets/card-logo.svg';
import DashboardBanner from './assets/dashboard-banner.svg';
import DashboardIllustration from './assets/dashboard-illustration.svg';
import LoginBackground from './assets/login-background.svg';
import LogoIcon from './assets/logo.svg';
import SmallLogoIcon from './assets/minify-logo.svg';
import SidebarLogoIcon from './assets/sidebar-logo.svg';
import SignInLogo from './assets/signIn-logo.svg';
import VerificationBanner from './assets/verification_bg_illustration.svg';

// Colors are mapped from the Nationwide Figma variables ("Default" and "Contrast" modes)
const buttonColors = {
  // Contrast mode (dark surfaces)
  onDarkButtonPrimary: '#2E69FF',
  onDarkButtonPrimaryHover: '#4D80FF',
  onDarkButtonPrimaryActive: '#002DA2',
  onDarkButtonPrimaryPressed: '#002DA2',
  onDarkButtonPrimaryDisabled: '#1A2E7A',

  onDarkButtonSecondaryHoverBorder: '#2E69FF',
  onDarkButtonSecondaryActiveBorder: '#2E69FF',
  onDarkButtonSecondaryDisabledBorder: '#3F4467',
  onDarkButtonSecondaryDisabled: '#272D5B',
  onDarkButtonSecondaryHover: '#1A2E7A',

  onDarkModuleBackground: '#202656',
  onDarkBackground: '#141B4D',

  onDarkIconAccent: '#0047BB',
  onDarkIconDisabled: '#73767D',
  onDarkText: '#73767D',

  // Default mode
  buttonPrimary: '#0047BB',
  buttonPrimaryHover: '#002DA2',
  buttonPrimaryActive: '#141B4D',
  buttonPrimaryPressed: '#002DA2',
  buttonPrimaryDisabled: '#D7D7D7',

  buttonSecondaryHoverBorder: '#0047BB',
  buttonSecondaryActiveBorder: '#141B4D',
  buttonSecondaryDisabledBorder: '#D7D7D7',
  buttonSecondaryDisabled: '#FFFFFF',
  buttonSecondaryHover: '#F3FAFF',

  buttonTertiaryHover: '#F3FAFF',
  buttonTertiaryPressed: '#93CFFB',
  buttonTertiaryDisabled: '#D7D7D7',
  buttonSecondaryContainer: '#FFFFFF',
};
const navigationColors = {
  navigationBackground: '#141A42',
  navigationBorder: '#2D315E',
  navigationAccent: '#9FE079',
  navigationAccent2: '#9FE079',
  navigationTextDefault: '#FFFFFF',
  navigationTextAccent: '#FFFFFF',
  navigationIconDefault: '#FFFFFF',
  navigationIconAccent: '#FFFFFF',
  navigationAccentHover: '#333C72',
  navigationAccentActive: '#2C3155',
};
const backgroundColors = {
  background1: '#141B4D',
  background2: '#141B4D',
  background3: '#000000',

  border: '#7B7B7B',
  border1: '#EBEBEB',
  border2: '#EFEFEF',
  module: '#FBFAFA',
  canvas: '#FFFFFF',
  stressContainer: '#333333',
  accentActive: '#0047BB',
  accentBorder: '#93CFFB',
  accentContainer: '#F3FAFF',
  textHighlighter: '#FFFF80',
  popupBg2: '#0F16210D',
  legend: '#B7DDFA',
};

const statusColors = {
  lockedNull: '#000000',
  processingPending: '#F9C134',
  needsHelp: '#FF9D0A',
  paidActiveComplete: '#19B269',
  partiallyPaid: '#B9CC29',
  waitingForFunds: '#8EB2A1',
  waitingForFund: '#8EB2A1',
  cobra: '#8EB2A1',
  deniedErrorExpired: '#E72626',
  uploading: '#9BA3B3',
  uploadedApproved: '#176AF6',
  loa: '#00B5EE',
  retirement: '#CE8258',
  future: '#7909AD',

  lockedNullContainer: '#E6E6E6',
  processingPendingContainer: '#FEF9EB',
  needsHelpContainer: '#FFF5E6',
  paidActiveCompleteContainer: '#E8F7F0',
  partiallyPaidContainer: '#F8FAEA',
  deniedErrorExpiredContainer: '#FDE9E9',
  uploadingContainer: '#F5F6F7',
  waitingForFundContainer: '#F4F7F6',
  uploadedApprovedContainer: '#E8F0FE',
  loaContainer: '#E6F8FD',
  retirementContainer: '#FAF3EE',
  futureContainer: '#F2E6F7',
};

const fileManagerColors = {
  cencusType: '#323D9B',
  enrollmentType: '#C64B8A',
  contributionType: '#0F7970',
  carrierType: '#0C95D0',
  mixType: '#481A54',
  unknownType: '#E72626',
  adjustment: '#588C0E',
};

const baseColors = {
  ...buttonColors,
  ...navigationColors,
  ...backgroundColors,
  ...statusColors,
  ...fileManagerColors,

  textTitle: '#171717',
  textBody: '#171717',

  textSecondary: '#2B2B2B',
  textDisabled: '#8C8C8C',
  textOnColor: '#FFFFFF',
  textAccent: '#0047BB',
  textAccentHover: '#002DA2',
  textAccentActive: '#141B4D',

  iconPrimary: '#171717',
  iconSecondary: '#A4A4A4',
  iconOnColor: '#FFFFFF',
  iconAccent: '#0047BB',
  iconAccentHover: '#002DA2',
  iconAccentActive: '#141B4D',
  iconDisabled: '#D7D7D7',

  success: '#19B269',
  successBorder: '#B8E7D1',
  successContainer: '#E8F7F0',

  warning: '#F9C134',
  warningBorder: '#FDECC0',
  warningContainer: '#FEF9EB',

  danger: '#E72626',
  dangerBorder: '#F8BCBC',
  dangerContainer: '#FDE9E9',
  dangerHover: '#A61C18',
  dangerActive: '#66120A',

  info: '#176AF6',
  infoBorder: '#B7D1FC',
  infoContainer: '#E8F0FE',

  transactionMedical: '#00C3B3',
  transactionDental: '#176AF6',
  transactionVision: '#481A54',
  transactionOther: '#FF9D0A',
  transactionParking: '#323D9B',
  transactionTransit: '#9BBF84',
  transactionAdoption: '#0F7970',
  transactionDependentCare: '#CC3381',
  transactionPharmacy: '#D83B00',
  transactionLifestyle: '#D3375C',
  transactionPremium: '#3628D1',
  transactionTravel: '#F26B31',
  transactionWellness: '#9B31F2',
  transactionPlanSpecific: '#00B5EE',
  refund: '#588C0E',
  transactionDefault: '#5E697E',
  claimTransferInternal: '#E5E9F0',
  nonClaimIcon: '#151F6D',
  nonClaimBg: '#E5E9F0',

  planTypeHSA: '#2EA12B',
  planTypeHCFSA: '#FA802A',
  planTypeDCAP: '#CC3381',
  planTypeHRA: '#134E8F',
  planTypeCommuterTransit: '#9BBF84',
  planTypeCommuterParking: '#323D9B',
  planTypeAdoption: '#0F7970',
  planTypeLifestyle: '#D3375C',
  planTypeSpecialty: '#00B5EE',
  planTypeTravel: '#F26B31',
  planTypeRx: '#7909AD',
  planTypeDental: '#176AF6',
  planTypeVision: '#481A54',
  planTypeMedical: '#00C3B3',

  missingDetail: '#FB6D2A',
  // #FB6D2A at 8% opacity flattened over white
  missingContainer: '#FFF3EE',
  missingBorder: '#FEDACA',

  investmentsfuture: '#7909AD',
  investmentsFund1: '#3F68FF',
  investmentsFund2: '#10D8B9',
  investmentsFund3: '#FB5C20',
  investmentsFund4: '#FC990F',
  investmentsFund5: '#FDD137',
  investmentsFund6: '#BCD1EF',
  investmentsFund7: '#323D9B',
  investmentsFund8: '#F3C626',
  investmentsFund9: '#A528C6',
  investmentsFund10: '#0C95D0',
  investmentsFund11: '#619C00',
  investmentsFund12: '#CC3381',
  investmentBackground: '#141B4D',
  profitContainer: '#00FF9314',
  lossContainer: '#EF235D33',
  chart: '#0047BB',
  whatIsNew1: '#025248',
  whatIsNew2: '#6C9EE8',
  whatIsNew3: '#A99880',

  partner: '#D83B00',
  distributor: '#904D27',
  employer: '#174FBC',
  subsidiary: '#0F7970',
  subgroup: '#481A54',
  claimProcessor: '#176AF6',
  employee: '#0CAD09',
  dependent: '#5E1696',
  noAssignee: '#B8BFCC',

  claimParticipant: '#CC3381',
  claimSystem: '#18A0FB',
  claimCardProcessor: '#19B269',

  ancor: '#0047BB',
  ancorHover: '#002DA2',
  activeButtonText: '#0047BB',
  paginationActiveItem: '#0047BB',

  tableHeader: '#171717',

  iconActive: '#0047BB',
  iconActiveHover: '#002DA2',

  investmentsProfit: '#00FF93',
  investmentsLoss: '#EF235D',
  sidebarDelimiter: '#ECECEC',
};
export default {
  title: 'Admin Nationwide',
  loginIconWidth: 300,
  loginIconHeight: 86,
  loginScreenTitle:
    'Radically transforming the consumer benefit experience with an ultra-reliable, automated, and AI-powered solution. ',
  colors: {
    ...baseColors,
    loginTitleColor: baseColors.textOnColor,
    popup: 'rgba(15, 22, 33, 0.5)',
    popup2: 'rgba(15, 22, 33, 0.05)',
    fade1: 'linear-gradient(0deg, #F7F9FA 0%, rgba(255, 255, 255, 0.00) 100%)',
    fade2: 'linear-gradient(180deg, #F7F9FA 0%, #EEF1F4 100%)',
  },
  logo: LogoIcon,
  minifyLogo: SmallLogoIcon,
  font: 'Helvetica, Arial, sans-serif',
  titleFont: "'Tiempos', Helvetica, serif",
  tetriaryAccentButton: true,
  images: {
    banner: DashboardBanner,
    cardBackground: CardBackground,
    loginBackground: LoginBackground,
    cardLogo: CardLogo,

    sidebarMinifyLogo: SmallLogoIcon,
    qrCodeLogo: SmallLogoIcon,
    investmentsBannerRight: Empty,
    reimburseMeSidebar: Empty,
    verificationBackground: VerificationBanner,
    signInLogo: SignInLogo,
    manageProfileBannerBackground: Empty,
    sidebarLogo: SidebarLogoIcon,
    bannerCloud: Empty,
    lgLoginBackground: LoginBackground,
    loginBackgroundClouds: Empty,
    expressSetupBannerBackground: Empty,

    fullDashboardIllustration: '',
    dashboardIllustration: DashboardIllustration,
  },
  fonts: {
    sidebarWeight: 500,
    sidebarFontSize: '14px',
    reimbursementWeight: 500,
    sizes: {
      small: {
        size: '12px',
        height: '16px',
        maxWidth: '336px',
      },
      '3xl': {
        size: '30px',
        height: '40px',
        maxWidth: '1008px',
      },
      '2xl': {
        size: '24px',
        height: '30px',
        maxWidth: '816px',
      },
    },
  },
  rounds: {
    moduleRound: '0px',
    container1Round: '0px',
    container2Round: '0px',
    checkboxRound: '0px',
    fieldRound: '0px',

    navigationButtonRound: '0px',
    button1Round: '4px',
    button2Round: '4px',
    dropdownItemRound: '0px',
    snackbarRound: '0px',
  },

  shadows: {
    default: '0px 6px 15px rgba(0, 0, 0, 0.04);',
    hover: '0px 8px 15px rgba(0, 0, 0, 0.10);',
    active: '0px 8px 25px rgba(0, 27, 114, 0.25);',
    highlighted: '0px 6px 16px rgba(0, 71, 187, 0.5);',
  },
  favicon: '/nationwide/favicon.ico',
  favicon16: '/nationwide/favicon-16x16.png',
  favicon32: '/nationwide/favicon-32x32.png',
  faviconAppleTouch: '/nationwide/apple-touch-icon.png',
} as WhiteLabelTheme;
